import React, { forwardRef } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { toast } from "sonner";

import { offerPlansToApi } from "./offerAdapter";
import OfferAPI from "../../api/OfferAPI";
import { FormProviderWithTranslations } from "../../contexts/FormTranslationsContext";
import {
  apiToFormTranslations,
  createTranslationResponseToFormField,
  formToApiTranslations,
  getOfferTranslationsFormSchema,
} from "../../utils/translationAdapters/offer";
import Input from "../form/Input";
import MarkdownEditorField from "../form/MarkdownEditorField";
import DeletePicturesButton from "../formTranslations/DeletePicturesButton";
import FormPictures from "../formTranslations/FormPictures";
import FormTranslations from "../formTranslations/FormTranslations";
import FormTranslationsHeader from "../formTranslations/FormTranslationsHeader";

import type {
  CreateOfferTranslationResponse,
  GetOfferResponse,
  UpdatedOffer,
} from "../../api/OfferAPI.types";
import type { LanguageCode } from "../../api/ProjectAPI.types";
import type { PictureKeys } from "../../constants/ratio";
import type {
  FormTranslationsHandle,
  PageFieldArray,
} from "../../contexts/FormTranslationsContext";
import type { OfferTranslationFormField } from "../../utils/translationAdapters/offer";
import type { UploadPictureState } from "../pictures/ModuleImageUploadWithTranslation";

export type OfferTranslationsHandle = FormTranslationsHandle<
  ReturnType<typeof formToApiTranslations>
>;

interface OfferTranslationsProps {
  offer: GetOfferResponse;
}

const OfferTranslations = forwardRef<
  OfferTranslationsHandle,
  OfferTranslationsProps
>(({ offer }, ref) => {
  const { t } = useTranslation("form");
  const { projectSlug } = useParams<{ projectSlug: string }>();

  const queryClient = useQueryClient();

  const getOfferQuery = OfferAPI.generateQuery({
    fn: OfferAPI.getOffer,
    params: [{ offerId: offer.uuid, projectSlug }],
  });

  const methods = useForm<
    PageFieldArray<OfferTranslationFormField>,
    unknown,
    PageFieldArray<OfferTranslationFormField>
  >({
    mode: "onChange",
    resolver: zodResolver(getOfferTranslationsFormSchema()),
    defaultValues: {
      translations: apiToFormTranslations(offer.translations),
    },
  });

  const uploadPicture = useMutation({
    mutationFn: async ({
      pictureFile,
      translationId,
      pictureFormat,
    }: {
      pictureFile: File;
      translationId: string;
      pictureFormat: PictureKeys;
    }) =>
      OfferAPI.uploadPicture({
        offerId: offer.uuid,
        pictureFile,
        translationId,
        pictureFormat,
        projectSlug,
      }),
    onSuccess(data, variables) {
      const updatedTranslations = offer.translations.map((translation) => {
        if (translation.uuid === variables.translationId) {
          return { ...translation, picture: data.picture };
        }
        return translation;
      });

      queryClient.setQueryData(getOfferQuery.queryKey, {
        ...offer,
        translations: updatedTranslations,
      });

      const formTranslations = methods.getValues("translations");
      const formIndex = formTranslations.findIndex(
        (translation) => translation.uuid === variables.translationId,
      );

      if (formIndex >= 0) {
        methods.setValue(`translations.${formIndex}.picture`, data.picture);
      }
    },
    onError: () => {
      toast.error(t("genericError", { ns: "error" }));
    },
  });

  const handlePictureUpload = ({
    file,
    translationId,
    pictureFormat,
  }: {
    file: File;
    translationId: string;
    pictureFormat: PictureKeys;
  }): void => {
    uploadPicture.mutate({ pictureFile: file, translationId, pictureFormat });
  };

  const uploadPictureState: UploadPictureState = {
    isLoading: uploadPicture.isLoading,
    pictureFormat: uploadPicture.variables?.pictureFormat,
    translationId: uploadPicture.variables?.translationId,
  };

  const deletePicture = useMutation({
    mutationFn: async ({ translationId }: { translationId: string }) =>
      OfferAPI.deletePicture({
        offerId: offer.uuid,
        translationId,
        projectSlug,
      }),
    onSuccess(_data, variables) {
      const updatedTranslations = offer.translations.map((translation) =>
        translation.uuid === variables.translationId
          ? { ...translation, picture: null }
          : translation,
      );

      queryClient.setQueryData(getOfferQuery.queryKey, {
        ...offer,
        translations: updatedTranslations,
      });

      const formTranslations = methods.getValues("translations");
      const formIndex = formTranslations.findIndex(
        (translation) => translation.uuid === variables.translationId,
      );

      if (formIndex >= 0) {
        methods.setValue(`translations.${formIndex}.picture`, undefined);
      }

      toast.success(t("form:deletePicturesSuccess"));
    },
    onError: () => {
      toast.error(t("genericError", { ns: "error" }));
    },
  });

  const handleAddLanguage = async ({
    languageCode,
    languageUuid,
  }: {
    languageCode: LanguageCode;
    languageUuid: string;
  }): Promise<CreateOfferTranslationResponse | null> => {
    if (!languageUuid) return null;

    try {
      const response = await OfferAPI.createTranslation({
        offerId: offer.uuid,
        languageUuid,
        projectSlug,
      });

      queryClient.setQueryData(getOfferQuery.queryKey, {
        ...offer,
        translations: [
          ...offer.translations,
          {
            uuid: response.uuid,
            default: response.default,
            description: response.description ?? "",
            language_uuid: response.language_uuid,
            language: languageCode,
            name: response.name ?? "",
            picture: null,
            pictures: null,
            temp_pictures: null,
          },
        ],
      });

      return response;
    } catch (error) {
      console.error("Error creating translation:", error);
      toast.error(t("genericError", { ns: "error" }));
      return null;
    }
  };

  const updateOfferMutation = useMutation({
    mutationFn: async (offerData: UpdatedOffer) =>
      OfferAPI.updateOffer({ offer: offerData, projectSlug }),
    onSuccess: () => {
      toast.success(t("form:deleteLanguageSuccess"));
      queryClient.invalidateQueries({ queryKey: getOfferQuery.queryKey });
    },
    onError: () => {
      toast.error(t("genericError", { ns: "error" }));
    },
  });

  const handleDeleteLanguage = async (
    updatedFormTranslations: ReturnType<typeof apiToFormTranslations>,
  ): Promise<void> => {
    const apiTranslations = formToApiTranslations(updatedFormTranslations);

    await updateOfferMutation.mutateAsync({
      ...offer,
      offer_plans: offerPlansToApi(offer.offer_plans),
      translations: apiTranslations.translations,
      translations_indexes: apiTranslations.translations_indexes,
    });
  };

  return (
    <div className="bg-card flex flex-col rounded-[0.3125rem] p-[1.5625rem] drop-shadow-md">
      <FormProviderWithTranslations
        formToApiTranslations={formToApiTranslations}
        handleRef={ref}
        methods={methods}
        onAddLanguage={handleAddLanguage}
        onDeleteLanguage={handleDeleteLanguage}
        toFormField={createTranslationResponseToFormField}
      >
        <FormTranslationsHeader />
        <FormTranslations>
          <Input
            isFullWidth
            isRequired
            label={t("fields.title")}
            name="name"
            type="text"
          />
          <MarkdownEditorField
            isFullWidth
            isRequired
            label={t("form:fields.description")}
            name="description"
          />
        </FormTranslations>
        <div>
          <div className="mb-0 flex items-center gap-[0.625rem]">
            <div className="font-semibold whitespace-nowrap md:whitespace-normal">
              {t("form:fields.pictures")} ({t("form:fields.picturesLimitation")}
              )
            </div>
            <DeletePicturesButton onDelete={deletePicture.mutateAsync} />
          </div>
          <FormPictures
            item={offer}
            itemType="offers"
            onUpload={handlePictureUpload}
            uploadPictureState={uploadPictureState}
          />
        </div>
      </FormProviderWithTranslations>
    </div>
  );
});

OfferTranslations.displayName = "OfferTranslations";

export default OfferTranslations;
